import React from 'react';
import Select, {
  components,
  GroupedOptionsType,
  IndicatorProps,
  OptionsType,
  ValueType,
  createFilter,
} from 'react-select';
import styled from 'styled-components';
import ErrorShower from '../ui-utils/error-shower';
import {useIntl} from "react-intl";

const Wrapper = styled.div``;

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
const BoldSingleValue = ({ children, ...props }: any): any => {
  return (
    <components.SingleValue {...props}>
      <b>{children}</b>
    </components.SingleValue>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DropdownIndicatorBlack = (props: IndicatorProps<any>): any => {
  return (
    <components.DropdownIndicator {...props}>
      <svg width="8" height="4" viewBox="0 0 8 4" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M7.42941 0.428571C7.42941 0.194196 7.23521 0 7.00084 0H1.00084C0.766462 0 0.572266 0.194196 0.572266 0.428571C0.572266 0.542411 0.619141 0.649553 0.699498 0.729911L3.6995 3.72991C3.77986 3.81027 3.887 3.85714 4.00084 3.85714C4.11468 3.85714 4.22182 3.81027 4.30218 3.72991L7.30218 0.729911C7.38253 0.649553 7.42941 0.542411 7.42941 0.428571Z"
          fill="#2E2E2E"
        />
      </svg>
    </components.DropdownIndicator>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DropdownIndicatorWhite = (props: IndicatorProps<any>): any => {
  return (
    <components.DropdownIndicator {...props}>
      <svg width="8" height="4" viewBox="0 0 8 4" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M7.42941 0.428571C7.42941 0.194196 7.23521 0 7.00084 0H1.00084C0.766462 0 0.572266 0.194196 0.572266 0.428571C0.572266 0.542411 0.619141 0.649553 0.699498 0.729911L3.6995 3.72991C3.77986 3.81027 3.887 3.85714 4.00084 3.85714C4.11468 3.85714 4.22182 3.81027 4.30218 3.72991L7.30218 0.729911C7.38253 0.649553 7.42941 0.542411 7.42941 0.428571Z"
          fill="white"
        />
      </svg>
    </components.DropdownIndicator>
  );
};

// this one below fixes slow large list
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Option = ({ children, ...props }: any): any => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { onMouseMove, onMouseOver, ...rest } = props.innerProps;
  const newProps = Object.assign(props, { innerProps: rest });
  return <components.Option {...newProps}>{children}</components.Option>;
};

const StyledReactSelect = (props: {
  options?: // eslint-disable-next-line @typescript-eslint/no-explicit-any
  OptionsType<{ value: any; label: string }> | GroupedOptionsType<{ value: string; label: string }>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (selectedValue: any) => void;
  className?: string;
  isMulti?: boolean;
  type?: 'blur' | 'thin-ice' | 'matt';
  searchable?: boolean;
  defaultValue?: ValueType<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any;
    label: string;
  }>;
  disabled?: boolean;
  error?: string;
  placeholder?: string;
  bold?: boolean;
  isLoading?: boolean;
  value?: ValueType<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any;
    label: string;
  }>;
}): JSX.Element => {
  const intl = useIntl()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChange = (selected: ValueType<{ value: any; label: string }>): void => {
    if (props.isMulti) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const value = (selected as { value: any; label: string }[])?.map((o) => o.value) as
        | string[]
        | number[];
      props.onChange(value !== undefined ? value : []);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const value = (selected as any)?.value;
      props.onChange(value !== undefined ? value : '');
    }
  };

  let typeClassName: string;
  let mattExtraClasses = '';

  switch (props.type) {
    case 'blur':
      typeClassName = 'react-select-container-blur';
      break;
    case 'thin-ice':
      typeClassName = 'react-select-container-light';
      break;
    default:
      typeClassName = 'react-select-container';
      mattExtraClasses = 'w-1/1 mb-3';
  }

  return (
    <Wrapper
      className={
        (mattExtraClasses ? mattExtraClasses + ' ' : '') +
        (props.disabled ? 'cursor-not-allowed ' : '') +
        (props.className || '')
      }
    >
      <Select
        isMulti={props.isMulti}
        placeholder={props.placeholder}
        onChange={handleChange}
        defaultValue={props.defaultValue}
        options={props.options}
        value={props.value}
        isSearchable={!!props.searchable}
        noOptionsMessage={() => intl.formatMessage({ id: 'common.noOptions' })}
        classNamePrefix="react-select"
        className={typeClassName}
        filterOption={createFilter({ ignoreAccents: false })}
        isLoading={props.isLoading}
        components={{
          DropdownIndicator:
            props.type === 'thin-ice' ? DropdownIndicatorBlack : DropdownIndicatorWhite,
          IndicatorSeparator: () => null,
          SingleValue: props.bold ? BoldSingleValue : components.SingleValue,
          Option,
        }}
        isDisabled={props.disabled}
      />
      <ErrorShower error={props.error} />
    </Wrapper>
  );
};

export default StyledReactSelect;
